import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Utensils, 
  Search, 
  Filter, 
  Clock, 
  Users, 
  FileText,
  ChevronRight,
  Download,
  MoreHorizontal,
  Star,
  Timer,
  Scale,
  Tag,
  Eye,
  Pizza,
  Coffee,
  Sandwich,
  Soup,
  Salad,
  Dessert,
  Plus,
  ArrowUpRight,
  SlidersHorizontal
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { sopData } from '../../data/sopData';
import ViewToggle from '../../../../components/shared/ViewToggle';
import { exportRecipeToPDF } from '../../../../utils/pdfExport';
import RecipeCard from './RecipeCard';

// Category Icons mapping
const categoryIcons = {
  Pizza: Pizza,
  Sandwiches: Sandwich,
  Appetizers: Soup,
  Salads: Salad,
  Desserts: Dessert,
  Beverages: Coffee
};

const getCategoryColor = (category) => {
  switch (category.toLowerCase()) {
    case 'pizza': return 'blue';
    case 'sandwiches': return 'green';
    case 'appetizers': return 'amber';
    case 'salads': return 'emerald';
    case 'desserts': return 'pink';
    case 'beverages': return 'purple';
    default: return 'gray';
  }
};

const handleDownload = (recipe) => {
  try {
    const doc = exportRecipeToPDF(recipe);
    doc.save(`${recipe.title.toLowerCase().replace(/\s+/g, '-')}-recipe.pdf`);
  } catch (error) {
    console.error('Failed to download recipe:', error);
    // Add error notification here
  }
};

const RecipeListItem = ({ recipe }) => (
  <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all p-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl bg-${getCategoryColor(recipe.category)}-50`}>
          <Utensils className={`w-5 h-5 text-${getCategoryColor(recipe.category)}-600`} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{recipe.title}</h3>
          <div className="flex items-center gap-4 mt-1">
            <span className="text-sm text-gray-500">{recipe.category}</span>
            <span className="text-sm text-gray-500">•</span>
            <span className="text-sm text-gray-500">Version {recipe.version}</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {recipe.prepTime}
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            {recipe.yield}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleDownload(recipe)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <Download className="w-4 h-4" />
          </button>
          <Link
            to={`/sop/recipes/${recipe.id}`}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            View Recipe
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const RecipesTab = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    prepTime: 'all',
    status: 'all',
    sortBy: 'recent'
  });

  // Get unique categories from recipes
  const categories = ['all', ...new Set(sopData.recipes.map(recipe => recipe.category))];

  const FilterPanel = () => (
    <motion.div 
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="bg-white rounded-xl shadow-sm p-6 mb-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Prep Time</label>
          <select
            value={filters.prepTime}
            onChange={(e) => setFilters({ ...filters, prepTime: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Times</option>
            <option value="quick">Quick (under 15 mins)</option>
            <option value="medium">Medium (15-30 mins)</option>
            <option value="long">Long (over 30 mins)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
          <select
            value={filters.sortBy}
            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="recent">Most Recent</option>
            <option value="name">Name</option>
            <option value="prepTime">Prep Time</option>
            <option value="popular">Most Used</option>
          </select>
        </div>
      </div>
    </motion.div>
  );

  const CategoryBar = () => (
    <div className="flex overflow-x-auto pb-4 hide-scrollbar">
      <div className="flex space-x-4">
        {categories.map(category => {
          const IconComponent = categoryIcons[category] || Utensils;
          return (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`flex items-center px-4 py-2 rounded-full transition-all ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <IconComponent className="w-4 h-4 mr-2" />
              <span className="whitespace-nowrap">
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Recipes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage and explore standardized recipes across all locations
          </p>
        </div>
        <div className="flex items-center gap-4">
          <ViewToggle view={view} onViewChange={setView} />
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <SlidersHorizontal className="w-5 h-5" />
            Filters
          </button>
          <Link
            to="/sop/recipes/new"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Recipe
          </Link>
        </div>
      </div>

      {/* Search and Category Bar */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search recipes by name, category, or ingredients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <CategoryBar />
      </div>

      {/* Filters Panel */}
      <AnimatePresence>
        {showFilters && <FilterPanel />}
      </AnimatePresence>

      {/* Content */}
      <AnimatePresence mode="wait">
        {view === 'grid' ? (
          <motion.div 
            key="grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {sopData.recipes
              .filter(recipe => {
                const matchesSearch = recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                    recipe.category.toLowerCase().includes(searchQuery.toLowerCase());
                const matchesCategory = selectedCategory === 'all' || recipe.category === selectedCategory;
                return matchesSearch && matchesCategory;
              })
              .map(recipe => (
                <RecipeCard 
                  key={recipe.id} 
                  recipe={recipe}
                  onDownload={handleDownload}
                />
              ))}
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {sopData.recipes
              .filter(recipe => {
                const matchesSearch = recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                    recipe.category.toLowerCase().includes(searchQuery.toLowerCase());
                const matchesCategory = selectedCategory === 'all' || recipe.category === selectedCategory;
                return matchesSearch && matchesCategory;
              })
              .map(recipe => (
                <RecipeListItem key={recipe.id} recipe={recipe} />
              ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RecipesTab; 